import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
  isFuturesModeEnabled,
  setFuturesModeEnabled,
  getSelectedFutures,
  setSelectedFutures as setSelectedFuturesGlobal,
  getOptionsUnderlying,
  getSelectedUnderlyings,
  convertPriceToFutures,
  getFuturesLabelForUnderlying,
  getFuturesByCategory,
  filterDataForFuturesMode,
  applyFuturesConversionToArray,
  getMappingStatus,
  setSymbolMappingEnabled,
  isSymbolMappingEnabled,
  fetchLiveMultipliers,
  getMultiplierAge,
  getCurrentMultipliers,
  type FuturesMapping,
} from '../utils/unifiedSymbolMapping';

interface SymbolMappingContextType {
  isFuturesMode: boolean;
  toggleFuturesMode: () => void;
  setFuturesMode: (enabled: boolean) => void;
  selectedFutures: string[];
  setSelectedFutures: (futures: string[]) => void;
  toggleFutureSelection: (ticker: string) => void;
  getOptionsUnderlyingForFuture: (futuresTicker: string) => string;
  getSelectedETFUnderlyings: () => string[];
  convertPrice: (price: number, etfUnderlying: string) => number;
  getFuturesLabel: (etfUnderlying: string) => string | null;
  futuresByCategory: Record<string, FuturesMapping[]>;
  filterData: (data: any[]) => any[];
  convertData: (data: any[]) => any[];
  mappingStatus: any;
  refreshMappingStatus: () => void;
  refreshMultipliers: (force?: boolean) => Promise<boolean>;
  multiplierAge: { ageMs: number; ageLabel: string; stale: boolean };
  multipliersRefreshing: boolean;
  currentMultipliers: Record<string, { ticker: string; multiplier: number; etf: string }>;
  // Backward compat
  isSymbolMappingEnabled: boolean;
  setIsSymbolMappingEnabled: (enabled: boolean) => void;
  toggleSymbolMapping: () => void;
}

const SymbolMappingContext = createContext<SymbolMappingContextType | undefined>(undefined);

export { SymbolMappingContext };

export const useSymbolMapping = () => {
  const context = useContext(SymbolMappingContext);
  if (context === undefined) {
    throw new Error('useSymbolMapping must be used within a SymbolMappingProvider');
  }
  return context;
};

interface SymbolMappingProviderProps {
  children: React.ReactNode;
}

export const SymbolMappingProvider: React.FC<SymbolMappingProviderProps> = ({ children }) => {
  const [futuresMode, setFuturesModeState] = useState<boolean>(isFuturesModeEnabled());
  const [selectedFutures, setSelectedFuturesState] = useState<string[]>(getSelectedFutures());
  const [mappingStatus, setMappingStatus] = useState<any>(getMappingStatus());
  const [multipliersRefreshing, setMultipliersRefreshing] = useState(false);
  const [multiplierAge, setMultiplierAge] = useState(getMultiplierAge());
  const [currentMultipliers, setCurrentMultipliers] = useState(getCurrentMultipliers());
  const hasAutoFetched = useRef(false);

  const setFuturesMode = useCallback((enabled: boolean) => {
    setFuturesModeEnabled(enabled);
    setSymbolMappingEnabled(enabled);
    setFuturesModeState(enabled);
    setMappingStatus(getMappingStatus());
  }, []);

  const toggleFuturesMode = useCallback(() => {
    setFuturesMode(!futuresMode);
  }, [futuresMode, setFuturesMode]);

  const setSelectedFutures_ = useCallback((futures: string[]) => {
    setSelectedFuturesGlobal(futures);
    setSelectedFuturesState(futures);
    setMappingStatus(getMappingStatus());
  }, []);

  const toggleFutureSelection = useCallback((ticker: string) => {
    const upper = String(ticker ?? '').trim().toUpperCase();
    if (!upper) return;
    const current = getSelectedFutures();
    const next = current.includes(upper)
      ? current.filter(f => f !== upper)
      : [...current, upper];
    setSelectedFutures_(next);
  }, [setSelectedFutures_]);

  const refreshMappingStatus = useCallback(() => {
    setMappingStatus(getMappingStatus());
  }, []);

  const refreshMultipliers = useCallback(async (force = false): Promise<boolean> => {
    setMultipliersRefreshing(true);
    try {
      const ok = await fetchLiveMultipliers(force);
      setMultiplierAge(getMultiplierAge());
      setCurrentMultipliers(getCurrentMultipliers());
      setMappingStatus(getMappingStatus());
      return ok;
    } finally {
      setMultipliersRefreshing(false);
    }
  }, []);

  // Auto-fetch multipliers when futures mode is turned ON (once per session, or if stale)
  useEffect(() => {
    if (!futuresMode) return;
    const age = getMultiplierAge();
    if (age.stale && !hasAutoFetched.current) {
      hasAutoFetched.current = true;
      refreshMultipliers(false);
    }
  }, [futuresMode, refreshMultipliers]);

  const value: SymbolMappingContextType = {
    isFuturesMode: futuresMode,
    toggleFuturesMode,
    setFuturesMode,
    selectedFutures,
    setSelectedFutures: setSelectedFutures_,
    toggleFutureSelection,
    getOptionsUnderlyingForFuture: getOptionsUnderlying,
    getSelectedETFUnderlyings: getSelectedUnderlyings,
    convertPrice: convertPriceToFutures,
    getFuturesLabel: getFuturesLabelForUnderlying,
    futuresByCategory: getFuturesByCategory(),
    filterData: filterDataForFuturesMode,
    convertData: applyFuturesConversionToArray,
    mappingStatus,
    refreshMappingStatus,
    refreshMultipliers,
    multiplierAge,
    multipliersRefreshing,
    currentMultipliers,
    // Backward compat
    isSymbolMappingEnabled: futuresMode,
    setIsSymbolMappingEnabled: setFuturesMode,
    toggleSymbolMapping: toggleFuturesMode,
  };

  return (
    <SymbolMappingContext.Provider value={value}>
      {children}
    </SymbolMappingContext.Provider>
  );
};
